// Detail Page
// Cinematic backdrop, floating poster, full anime information

import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  Stack,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Rating,
  IconButton,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlayArrow,
  Visibility,
  Bookmark,
  BookmarkBorder,
  Favorite,
  FavoriteBorder,
  StarRate,
  Close,
  Share,
  CalendarMonth,
  Tv,
  Movie as MovieIcon,
  Flag,
  MoreVert,
} from '@mui/icons-material';
import { fetchAnimeDetail, fetchEpisodes, Anime, Episode } from '../../hooks/useApi';
import { addRecentlyViewed } from '../../hooks/recent';
import { AnimeCard } from '../../components/ui/Card';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { DetailSkeleton } from '../../components/ui/Skeleton';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, toggleFavorite, toggleBookmark } = useAuth();

  const numId = parseInt(id || '0', 10);

  const { data, isLoading } = useQuery({
    queryKey: ['anime', numId],
    queryFn: () => fetchAnimeDetail(numId),
    enabled: !!numId,
  });

  const { data: episodes = [], isLoading: loadingEpisodes } = useQuery({
    queryKey: ['episodes', numId],
    queryFn: () => fetchEpisodes(numId, numId),
    enabled: !!numId,
  });

  const [tabValue, setTabValue] = React.useState(0);
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    if (data?.anime) {
      addRecentlyViewed({
        id: data.anime.id,
        title: data.anime.title,
        poster: data.anime.poster,
        type: data.anime.type,
      });
    }
  }, [data?.anime]);

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ pt: 8 }}>
        <DetailSkeleton />
      </Container>
    );
  }

  if (!data?.anime) {
    return (
      <Container maxWidth="xl" sx={{ pt: 12, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>404</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>Anime not found</Typography>
        <Button variant="contained" onClick={() => navigate('/')}>Go Home</Button>
      </Container>
    );
  }

  const anime = data.anime;
  const genres = (anime.genres || []).map(g => typeof g === 'string' ? g : g.name);
  const isFav = user?.favorites?.includes(anime.id);
  const isBook = user?.bookmarks?.includes(anime.id);

  const synopsis = anime.synopsis || 'No synopsis available.';
  const shouldClamp = synopsis.length > 400;

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Cinematic Backdrop */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 260, sm: 360, md: 480 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${anime.cover || anime.poster})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 20%',
            filter: 'brightness(0.5) saturate(0.9)',
            transform: 'scale(1.05)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, var(--anime-token-background-default) 5%, rgba(10,15,26,0.4) 50%, rgba(10,15,26,0.1) 100%)',
          }}
        />
      </Box>

      <Container maxWidth="xl" sx={{ position: 'relative', mt: { xs: -180, sm: -240, md: -280 }, pb: 6 }}>
        <Grid container spacing={4}>
          {/* Floating Poster */}
          <Grid item xs={12} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 40, rotateX: 10 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.5, ease: [0.175, 0.885, 0.32, 1.275] }}
            >
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '2/3',
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: '2px solid var(--anime-token-border-default)',
                  boxShadow: 'var(--anime-token-shadow-2xl)',
                  backgroundColor: 'var(--anime-token-surface-default)',
                }}
              >
                <img
                  src={anime.poster}
                  alt={anime.title}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.currentTarget.src = '/images/placeholder-poster.jpg'; }}
                />
                {anime.isAdult && (
                  <Chip
                    label="18+"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      backgroundColor: '#F97316',
                      color: 'white',
                      fontWeight: 700,
                      zIndex: 2,
                    }}
                  />
                )}
              </Box>

              {/* Action Buttons under poster (mobile) */}
              <Stack direction="column" spacing={1.5} sx={{ mt: 2 }}>
                <Button
                  variant="gradient"
                  fullWidth
                  size="large"
                  startIcon={<PlayArrow fontSize="inherit" />}
                  onClick={() => navigate(`/watch/${anime.id}?ep=1&title=${encodeURIComponent(anime.title)}`)}
                >
                  Watch Now
                </Button>
                <Stack direction="row" spacing={1.5}>
                  {user && (
                    <>
                      <Button
                        variant="soft"
                        fullWidth
                        startIcon={isBook ? <Bookmark fontSize="small" /> : <BookmarkBorder fontSize="small" />}
                        onClick={() => toggleBookmark(anime.id)}
                        color={isBook ? 'primary' : 'inherit'}
                      >
                        {isBook ? 'Saved' : 'Save'}
                      </Button>
                      <Button
                        variant="soft"
                        fullWidth
                        startIcon={isFav ? <Favorite fontSize="small" style={{ color: '#FBBF24' }} /> : <FavoriteBorder fontSize="small" />}
                        onClick={() => toggleFavorite(anime.id)}
                        color={isFav ? 'primary' : 'inherit'}
                      >
                        {isFav ? 'Liked' : 'Like'}
                      </Button>
                    </>
                  )}
                  <IconButton
                    sx={{ border: '1px solid var(--anime-token-border-default)' }}
                    aria-label="Share"
                  >
                    <Share fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </motion.div>
          </Grid>

          {/* Main Info */}
          <Grid item xs={12} md={9}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip label={anime.type} color="primary" size="small" sx={{ fontWeight: 600 }} />
                {anime.status && (
                  <Chip
                    label={anime.status}
                    size="small"
                    variant="outlined"
                    color={anime.status === 'RELEASING' ? 'success' : 'default'}
                  />
                )}
                {anime.isAdult && (
                  <Chip label="Adult" size="small" sx={{ backgroundColor: '#F97316', color: 'white', fontWeight: 700 }} />
                )}
              </Stack>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                  mb: 2,
                }}
              >
                {anime.title}
              </Typography>

              {/* Meta Row */}
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 3, alignItems: 'center' }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <StarRate sx={{ color: '#FBBF24', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{anime.score.toFixed(1)}</Typography>
                  <Typography variant="caption" color="text.secondary">/10</Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary">{anime.episodes} episodes</Typography>
                {anime.year && <Typography variant="body1" color="text.secondary">{anime.year}</Typography>}
                {anime.status && <Typography variant="body1" color="text.secondary">{anime.status}</Typography>}
              </Stack>

              {/* Genres */}
              {genres.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                  {genres.map((g, i) => (
                    <Chip
                      key={i}
                      label={g}
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`/browse?genre=${encodeURIComponent(g)}`)}
                      sx={{
                        cursor: 'pointer',
                        borderColor: 'var(--anime-token-border-default)',
                        color: 'var(--anime-token-text-secondary)',
                        '&:hover': { borderColor: 'var(--anime-token-brand-primary-main)', color: 'var(--anime-token-brand-primary-main)' },
                      }}
                    />
                  ))}
                </Stack>
              )}

              {/* Desktop Actions */}
              {!isMobile && (
                <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="gradient"
                    size="large"
                    startIcon={<PlayArrow fontSize="inherit" />}
                    onClick={() => navigate(`/watch/${anime.id}?ep=1&title=${encodeURIComponent(anime.title)}`)}
                    sx={{ px: 4 }}
                  >
                    Watch Episode 1
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Visibility fontSize="inherit" />}
                    sx={{ px: 4, borderColor: 'var(--anime-token-border-default)' }}
                  >
                    Watch Trailer
                  </Button>
                  {user && (
                    <>
                      <IconButton
                        onClick={() => toggleBookmark(anime.id)}
                        sx={{
                          border: '1px solid var(--anime-token-border-default)',
                          backgroundColor: isBook ? 'var(--anime-token-brand-primary-500_15)' : 'transparent',
                          color: isBook ? 'var(--anime-token-brand-primary-main)' : 'var(--anime-token-text-secondary)',
                        }}
                        aria-label="Bookmark"
                      >
                        {isBook ? <Bookmark /> : <BookmarkBorder />}
                      </IconButton>
                      <IconButton
                        onClick={() => toggleFavorite(anime.id)}
                        sx={{
                          border: '1px solid var(--anime-token-border-default)',
                          backgroundColor: isFav ? 'var(--anime-token-brand-primary-500_15)' : 'transparent',
                          color: isFav ? '#FBBF24' : 'var(--anime-token-text-secondary)',
                        }}
                        aria-label="Favorite"
                      >
                        {isFav ? <Favorite /> : <FavoriteBorder />}
                      </IconButton>
                    </>
                  )}
                  <IconButton sx={{ border: '1px solid var(--anime-token-border-default)' }} aria-label="Share">
                    <Share />
                  </IconButton>
                </Stack>
              )}

              {/* Synopsis */}
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  lineHeight: 1.7,
                  fontSize: '1rem',
                  maxWidth: 800,
                  display: '-webkit-box',
                  WebkitLineClamp: expanded ? 'unset' : shouldClamp ? 4 : 'unset',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {synopsis}
              </Typography>
              {shouldClamp && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  sx={{ mt: 1, color: 'var(--anime-token-brand-primary-main)' }}
                >
                  {expanded ? 'Show Less' : 'Read More'}
                </Button>
              )}
            </motion.div>

            {/* Tabs */}
            <Box sx={{ mt: 4, borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label="Episodes" />
                <Tab label="Characters" />
                <Tab label="Recommendations" />
                <Tab label="Reviews" />
              </Tabs>
            </Box>

            {/* Episodes Tab */}
            <TabPanel value={tabValue} index={0}>
              {loadingEpisodes ? (
                <Stack spacing={1}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={56} />
                  ))}
                </Stack>
              ) : episodes.length > 0 ? (
                <Grid container spacing={2}>
                  {episodes.map((ep: Episode, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={ep.id}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.02 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <Box
                          onClick={() => navigate(`/watch/${anime.id}?ep=${ep.number}&title=${encodeURIComponent(anime.title)}`)}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: 'var(--anime-token-surface-default)',
                            border: '1px solid var(--anime-token-border-default)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: 'var(--anime-token-brand-primary-main)',
                              backgroundColor: 'var(--anime-token-brand-primary-500_10)',
                            },
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                              variant="rounded"
                              src={ep.image}
                              sx={{ width: 64, height: 36, bgcolor: 'var(--anime-token-surface-variant)' }}
                            >
                              {ep.number}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle2" fontWeight={600} noWrap>
                                Episode {ep.number}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {ep.title}
                              </Typography>
                            </Box>
                            <PlayArrow sx={{ color: 'var(--anime-token-brand-primary-main)', flexShrink: 0 }} />
                          </Stack>
                        </Box>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.tertiary' }}>
                  <Tv sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">No episodes available</Typography>
                </Box>
              )}
            </TabPanel>

            {/* Characters Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ textAlign: 'center', py: 6, color: 'text.tertiary' }}>
                <Typography variant="h6">Character information coming soon</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>We're working on bringing you detailed character and voice actor data.</Typography>
              </Box>
            </TabPanel>

            {/* Recommendations Tab */}
            <TabPanel value={tabValue} index={2}>
              {data.relations && data.relations.length > 0 ? (
                <Grid container spacing={3}>
                  {data.relations.map((rel, index) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={rel.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.03 }}
                      >
                        <AnimeCard
                          id={rel.id}
                          title={rel.title}
                          poster={rel.poster}
                          score={rel.episodes || 0}
                          type={rel.type}
                          episodes={rel.episodes}
                          onClick={() => navigate(`/anime/${rel.id}`)}
                        />
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.tertiary' }}>
                  <MovieIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">No related anime found</Typography>
                </Box>
              )}
            </TabPanel>

            {/* Reviews Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ textAlign: 'center', py: 6, color: 'text.tertiary' }}>
                <StarRate sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6">Reviews coming soon</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>User reviews and ratings will appear here.</Typography>
              </Box>
            </TabPanel>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}